/* Collection Story Ribbon — vanilla custom element, zero dependencies */
(function () {
  if (customElements.get('collection-story')) return;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  class CollectionStory extends HTMLElement {
    connectedCallback() {
      this.band     = this.querySelector('.cdd-rib__band');
      this.toggle   = this.querySelector('[data-rib-toggle]');
      this.pauseBtn = this.querySelector('[data-rib-pause]');
      this.track    = this.querySelector('[data-rib-track]');
      this.group    = this.querySelector('[data-rib-group]');
      this.panel    = this.querySelector('[data-rib-panel]');
      this.closeBtn = this.querySelector('[data-rib-close]');
      if (!this.toggle || !this.panel) return;

      this.speed = parseFloat(this.dataset.speed || '45') || 45;
      this.isOpen = false;

      const updateWidth = () => {
        const docW = document.documentElement.clientWidth || window.innerWidth;
        this.style.setProperty('--doc-width', docW + 'px');
      };
      updateWidth();
      window.addEventListener('resize', updateWidth, { passive: true });

      this.toggle.addEventListener('click', () => this.setOpen(!this.isOpen));
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => {
          this.setOpen(false);
          this.toggle.focus({ preventScroll: true });
        });
      }
      if (this.pauseBtn) {
        this.pauseBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.setPaused(!this.classList.contains('is-paused'));
        });
      }

      this.initMarquee();
      reduced.addEventListener('change', () => this.initMarquee());

      if (location.hash === '#collection-story') this.setOpen(true);
    }

    /* ---- seamless marquee, constant speed at any content length ---- */
    initMarquee() {
      if (!this.track || !this.group) return;

      if (reduced.matches) {
        this.classList.add('is-static');
        this.classList.remove('is-running');
        return;
      }
      this.classList.remove('is-static');

      if (this.baseHTML === undefined) this.baseHTML = this.group.innerHTML;

      const build = () => {
        // Reset to one clean group
        this.track.querySelectorAll('[data-rib-clone]').forEach((n) => n.remove());
        this.group.innerHTML = this.baseHTML;

        const bandW = this.band.offsetWidth || window.innerWidth || 1;

        // Repeat the phrase set until one group is at least as wide as the band
        let guard = 0;
        while (this.group.offsetWidth < bandW && guard++ < 12) {
          this.group.insertAdjacentHTML('beforeend', this.baseHTML);
        }

        const groupW = this.group.offsetWidth;
        if (!groupW) return;

        // Second identical group makes the -50% loop seamless
        const clone = this.group.cloneNode(true);
        clone.removeAttribute('data-rib-group');
        clone.setAttribute('data-rib-clone', '');
        this.track.appendChild(clone);

        this.style.setProperty('--rib-dur', (groupW / this.speed).toFixed(2) + 's');
        this.classList.add('is-running');
      };

      // Fonts change text metrics — rebuild once loaded
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(build).catch(build);
      } else {
        build();
      }
      build();

      let t;
      window.addEventListener('resize', () => {
        clearTimeout(t);
        t = setTimeout(build, 200);
      }, { passive: true });

      // Pause the CSS animation when the ribbon is off-screen (saves GPU/battery)
      if ('IntersectionObserver' in window && !this.io) {
        this.io = new IntersectionObserver(
          (entries) => this.classList.toggle('is-offscreen', !entries[0].isIntersecting),
          { rootMargin: '120px 0px' }
        );
        this.io.observe(this);
      }
    }

    setOpen(next) {
      this.isOpen = next;
      this.classList.toggle('is-open', next);
      this.toggle.setAttribute('aria-expanded', String(next));

      if (next) {
        this.panel.removeAttribute('inert');
      } else {
        this.panel.setAttribute('inert', '');
        // If the ribbon scrolled above the fold while open, bring it back smoothly
        if (this.getBoundingClientRect().top < 0) {
          this.scrollIntoView({
            behavior: reduced.matches ? 'auto' : 'smooth',
            block: 'start'
          });
        }
      }

      this.dispatchEvent(
        new CustomEvent(next ? 'story:open' : 'story:close', { bubbles: true })
      );
    }

    setPaused(paused) {
      this.classList.toggle('is-paused', paused);
      if (!this.pauseBtn) return;
      this.pauseBtn.setAttribute('aria-pressed', String(paused));
      this.pauseBtn.setAttribute(
        'aria-label',
        paused ? 'Resume the scrolling text' : 'Pause the scrolling text'
      );
    }
  }

  customElements.define('collection-story', CollectionStory);
})();
